import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { profileAPI, authAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { 
  TreeDeciduous, 
  Truck, 
  ArrowRight, 
  ArrowLeft,
  CheckCircle2,
  MapPin,
  Briefcase,
  User
} from 'lucide-react';

const BUNDESLAENDER = [
  "Baden-Württemberg", "Bayern", "Berlin", "Brandenburg", "Bremen",
  "Hamburg", "Hessen", "Mecklenburg-Vorpommern", "Niedersachsen",
  "Nordrhein-Westfalen", "Rheinland-Pfalz", "Saarland", "Sachsen",
  "Sachsen-Anhalt", "Schleswig-Holstein", "Thüringen"
];

const SERVICES = [
  "Holzernte", "Rückung", "Aufforstung", "Waldpflege",
  "Harvesterarbeiten", "Manuelle Fällung", "Forwarder", 
  "Pflanzung", "Durchforstung", "Zaunbau"
];

const OnboardingPage = () => {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    company_name: '',
    bundesland: '',
    ort: '',
    phone: '',
    description: '',
    services: [],
    service_regions: [],
    experience_years: '',
  });

  const isWaldbesitzer = user?.role === 'waldbesitzer';
  const totalSteps = isWaldbesitzer ? 2 : 3;

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const toggleService = (service) => {
    setFormData(prev => ({
      ...prev,
      services: prev.services.includes(service)
        ? prev.services.filter(s => s !== service)
        : [...prev.services, service]
    }));
  };

  const toggleRegion = (region) => {
    setFormData(prev => ({
      ...prev,
      service_regions: prev.service_regions.includes(region)
        ? prev.service_regions.filter(r => r !== region)
        : [...prev.service_regions, region]
    }));
  };

  const handleComplete = async () => {
    setLoading(true);
    try {
      // Update profile
      const profileData = {
        bundesland: formData.bundesland,
        ort: formData.ort || null,
        phone: formData.phone || null,
        description: formData.description || null,
      };

      if (!isWaldbesitzer) {
        profileData.company_name = formData.company_name || null;
        profileData.services = formData.services;
        profileData.service_regions = formData.service_regions;
        profileData.experience_years = formData.experience_years ? parseInt(formData.experience_years) : null;
      }

      await profileAPI.updateMyProfile(profileData);
      await authAPI.completeOnboarding();
      await refreshUser();
      
      toast.success(t('toasts.willkommen'));
      navigate('/dashboard');
    } catch (error) {
      console.error('Onboarding error:', error);
      toast.error(t('toasts.einstellungenFehler'));
    } finally {
      setLoading(false);
    }
  };

  const renderStep = () => {
    // Step 1: Welcome & Basic Info
    if (step === 1) {
      return (
        <div className="space-y-6">
          <div className="text-center mb-8">
            <div className={`inline-flex p-4 rounded-2xl mb-4 ${isWaldbesitzer ? 'bg-primary/10' : 'bg-accent/10'}`}>
              {isWaldbesitzer ? (
                <TreeDeciduous className="h-12 w-12 text-primary" />
              ) : (
                <Truck className="h-12 w-12 text-accent" />
              )}
            </div>
            <h2 className="font-serif text-2xl font-bold text-gray-900 mb-2">
              Willkommen, {user?.name}!
            </h2>
            <p className="text-gray-600">
              {isWaldbesitzer 
                ? 'Richten Sie Ihr Waldbesitzer-Konto ein'
                : 'Richten Sie Ihr Anbieter-Profil ein'}
            </p>
          </div>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Bundesland *</Label>
              <Select
                value={formData.bundesland}
                onValueChange={(value) => handleChange('bundesland', value)}
              >
                <SelectTrigger data-testid="onboarding-bundesland">
                  <SelectValue placeholder={t('placeholders.bundeslandWaehlen')} />
                </SelectTrigger>
                <SelectContent>
                  {BUNDESLAENDER.map((land) => (
                    <SelectItem key={land} value={land}>{land}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Ort / Stadt</Label>
              <Input
                value={formData.ort}
                onChange={(e) => handleChange('ort', e.target.value)}
                placeholder="z.B. München"
                data-testid="onboarding-ort"
              />
            </div>

            <div className="space-y-2">
              <Label>Telefon</Label>
              <Input
                value={formData.phone}
                onChange={(e) => handleChange('phone', e.target.value)}
                placeholder="+49 ..."
                data-testid="onboarding-phone"
              />
            </div>
          </div>
        </div>
      );
    }

    // Step 2 for Waldbesitzer: About
    if (step === 2 && isWaldbesitzer) {
      return (
        <div className="space-y-6">
          <div className="text-center mb-8">
            <div className="inline-flex p-4 rounded-2xl bg-primary/10 mb-4">
              <User className="h-12 w-12 text-primary" />
            </div>
            <h2 className="font-serif text-2xl font-bold text-gray-900 mb-2">
              Über Sie
            </h2>
            <p className="text-gray-600">
              Optionale Informationen für Anbieter
            </p>
          </div>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Kurze Beschreibung (optional)</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => handleChange('description', e.target.value)}
                placeholder="z.B. Waldbesitzer mit 50 ha Mischwald in Bayern..."
                rows={4}
                data-testid="onboarding-description"
              />
            </div>
          </div>
        </div>
      );
    }

    // Step 2 for Anbieter: Company Info
    if (step === 2 && !isWaldbesitzer) {
      return (
        <div className="space-y-6">
          <div className="text-center mb-8">
            <div className="inline-flex p-4 rounded-2xl bg-accent/10 mb-4">
              <Briefcase className="h-12 w-12 text-accent" />
            </div>
            <h2 className="font-serif text-2xl font-bold text-gray-900 mb-2">
              Ihr Unternehmen
            </h2>
            <p className="text-gray-600">
              Informationen über Ihr Forstunternehmen
            </p>
          </div>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Firmenname</Label>
              <Input
                value={formData.company_name}
                onChange={(e) => handleChange('company_name', e.target.value)}
                placeholder="z.B. Waldservice Müller GmbH"
                data-testid="onboarding-company"
              />
            </div>

            <div className="space-y-2">
              <Label>Jahre Erfahrung</Label>
              <Input
                type="number"
                value={formData.experience_years}
                onChange={(e) => handleChange('experience_years', e.target.value)}
                placeholder="z.B. 15"
                data-testid="onboarding-experience"
              />
            </div>

            <div className="space-y-2">
              <Label>Beschreibung Ihres Unternehmens</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => handleChange('description', e.target.value)}
                placeholder={t('placeholders.unternehmenBeschreiben')}
                rows={4}
                data-testid="onboarding-description"
              />
            </div>
          </div>
        </div>
      );
    }

    // Step 3 for Anbieter: Services & Regions
    if (step === 3 && !isWaldbesitzer) {
      return (
        <div className="space-y-6">
          <div className="text-center mb-8">
            <div className="inline-flex p-4 rounded-2xl bg-accent/10 mb-4">
              <MapPin className="h-12 w-12 text-accent" />
            </div>
            <h2 className="font-serif text-2xl font-bold text-gray-900 mb-2">
              Leistungen & Regionen
            </h2>
            <p className="text-gray-600">
              Welche Arbeiten bieten Sie an und wo?
            </p>
          </div>

          <div className="space-y-6">
            <div className="space-y-3">
              <Label>Ihre Dienstleistungen</Label>
              <div className="grid grid-cols-2 gap-2">
                {SERVICES.map((service) => (
                  <div
                    key={service}
                    onClick={() => toggleService(service)}
                    className={`flex items-center gap-2 p-3 rounded-lg border cursor-pointer transition-colors ${
                      formData.services.includes(service)
                        ? 'bg-accent/10 border-accent'
                        : 'bg-white border-warm-200 hover:border-accent/50'
                    }`}
                  >
                    <Checkbox checked={formData.services.includes(service)} />
                    <span className="text-sm">{service}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="space-y-3">
              <Label>Einsatzgebiete (Bundesländer)</Label>
              <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto">
                {BUNDESLAENDER.map((region) => (
                  <div
                    key={region}
                    onClick={() => toggleRegion(region)}
                    className={`flex items-center gap-2 p-3 rounded-lg border cursor-pointer transition-colors ${
                      formData.service_regions.includes(region)
                        ? 'bg-accent/10 border-accent'
                        : 'bg-white border-warm-200 hover:border-accent/50'
                    }`}
                  >
                    <Checkbox checked={formData.service_regions.includes(region)} />
                    <span className="text-sm">{region}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      );
    }
  };

  const canProceed = () => {
    if (step === 1) return !!formData.bundesland;
    return true;
  };

  return (
    <div className="min-h-screen bg-warm-50 flex items-center justify-center p-4" data-testid="onboarding-page">
      <Card className="w-full max-w-lg">
        <CardHeader className="text-center">
          <div className="flex items-center justify-center gap-2 mb-4">
            <TreeDeciduous className="h-6 w-6 text-primary" />
            <span className="font-serif text-xl font-bold text-primary">ForestHub</span>
          </div>
          
          {/* Progress */}
          <div className="flex items-center justify-center gap-2">
            {Array.from({ length: totalSteps }).map((_, i) => (
              <div
                key={i}
                className={`h-2 w-12 rounded-full transition-colors ${
                  i + 1 <= step ? 'bg-primary' : 'bg-warm-300'
                }`}
              />
            ))}
          </div>
          <p className="text-sm text-gray-500 mt-2">
            Schritt {step} von {totalSteps}
          </p>
        </CardHeader>

        <CardContent>
          {renderStep()}

          <div className="flex items-center justify-between mt-8 pt-6 border-t border-warm-200">
            {step > 1 ? (
              <Button
                variant="ghost"
                onClick={() => setStep(step - 1)}
                disabled={loading}
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                Zurück
              </Button>
            ) : (
              <div />
            )}

            {step < totalSteps ? (
              <Button
                onClick={() => setStep(step + 1)}
                disabled={!canProceed()}
                className="bg-primary hover:bg-primary-600"
              >
                Weiter
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            ) : (
              <Button
                onClick={handleComplete}
                disabled={loading}
                className="bg-primary hover:bg-primary-600"
                data-testid="onboarding-complete-btn"
              >
                {loading ? (
                  t('toasts.wirdGespeichert')
                ) : (
                  <>
                    <CheckCircle2 className="mr-2 h-4 w-4" />
                    Abschließen
                  </>
                )}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default OnboardingPage;
